import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Switch } from "@headlessui/react";
import { Bell, Mail, Smartphone, AlertTriangle, CheckCircle2, ExternalLink } from "lucide-react";
import { Checkbox } from "../components/shadcn/checkbox";
import UserAction from "../actions/UserAction";
import LayoutAction from "../actions/LayoutAction";
import { getAppLink } from "../api/AppLinkApi";

/* ── Constants ── */

const NOTIFICATION_TYPES = [
  { key: "MENTION", labelKey: "Mentions", descKey: "When someone @mentions you in a comment" },
  { key: "REPLY", labelKey: "Replies", descKey: "When someone replies to your comment" },
  {
    key: "EMAIL_GRANT",
    labelKey: "Email Grants",
    descKey: "Email access requested or request accepted/denied",
  },
  { key: "TASK_ASSIGNMENT", labelKey: "Task Assignments", descKey: "When you are assigned a task" },
  { key: "AUTOMATION", labelKey: "Automation", descKey: "When your automation run completes" },
  {
    key: "SHARED_RESOURCE",
    labelKey: "Shared Resources",
    descKey: "When a resource is shared with you",
  },
] as const;

const DEBOUNCE_MS = 600;

/* ── Types ── */

type ChannelPrefs = { inApp: boolean; email: boolean };
type DailyDigestPrefs = { enabled: boolean; sendTime: string };
type EmailConnectionStatus = {
  loading: boolean;
  googleConnected: boolean;
  azureConnected: boolean;
};

/* ── Helpers ── */

function normalizeChannelPrefs(raw: unknown): ChannelPrefs {
  if (raw && typeof raw === "object" && "inApp" in raw && "email" in raw) {
    return {
      inApp: (raw as { inApp?: boolean }).inApp !== false,
      email: (raw as { email?: boolean }).email !== false,
    };
  }
  const on = raw !== false;
  return { inApp: on, email: on };
}

function buildPrefsFromSaved(saved: Record<string, unknown> | undefined) {
  const out: Record<string, ChannelPrefs> = {};
  for (const { key } of NOTIFICATION_TYPES) {
    out[key] = normalizeChannelPrefs(saved?.[key]);
  }
  return out;
}

function normalizeDailyDigest(raw: unknown): DailyDigestPrefs {
  if (raw && typeof raw === "object") {
    const candidate = raw as { enabled?: boolean; sendTime?: string };
    return {
      enabled: candidate.enabled !== false,
      sendTime:
        typeof candidate.sendTime === "string" && candidate.sendTime.trim().length > 0
          ? candidate.sendTime
          : "08:00",
    };
  }
  return { enabled: raw !== false, sendTime: "08:00" };
}

/* ── Custom Hook ── */

function useNotificationPreferences() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const notificationPrefsRaw = currentUser?.get?.("notificationPreferences");
  const savedPrefs = notificationPrefsRaw?.toJS?.() ?? notificationPrefsRaw;

  const [dailyDigest, setDailyDigest] = useState<DailyDigestPrefs>(() =>
    normalizeDailyDigest(savedPrefs?.DAILY_DIGEST)
  );
  const [prefs, setPrefs] = useState(() => buildPrefsFromSaved(savedPrefs));

  // Sync local state when server data changes
  useEffect(() => {
    setPrefs(buildPrefsFromSaved(savedPrefs));
    setDailyDigest(normalizeDailyDigest(savedPrefs?.DAILY_DIGEST));
  }, [notificationPrefsRaw]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced auto-save
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const persistPrefs = useCallback(
    (nextPrefs: Record<string, ChannelPrefs>, nextDigest: DailyDigestPrefs) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

      saveTimerRef.current = setTimeout(async () => {
        const payload: Record<string, unknown> = {};
        for (const { key } of NOTIFICATION_TYPES) {
          payload[key] = {
            inApp: nextPrefs[key]?.inApp !== false,
            email: nextPrefs[key]?.email !== false,
          };
        }
        payload.DAILY_DIGEST = {
          enabled: nextDigest.enabled,
          sendTime: nextDigest.sendTime,
        };

        await dispatch(UserAction.updateCurrentUserSetting({ notificationPreferences: payload }));
        dispatch(
          LayoutAction.setNotification({
            title: t("Notification settings"),
            type: "success",
            message: t("Notification preferences saved."),
          })
        );
      }, DEBOUNCE_MS);
    },
    [dispatch, t]
  );

  const toggleChannel = useCallback(
    (typeKey: string, channel: "inApp" | "email") => {
      setPrefs((prev) => {
        const current = prev[typeKey] ?? { inApp: true, email: true };
        const next = { ...prev, [typeKey]: { ...current, [channel]: !current[channel] } };
        persistPrefs(next, dailyDigest);
        return next;
      });
    },
    [persistPrefs, dailyDigest]
  );

  const toggleDailyDigest = useCallback(
    (val: boolean) => {
      const nextDigest = { ...dailyDigest, enabled: val };
      setDailyDigest(nextDigest);
      persistPrefs(prefs, nextDigest);
    },
    [persistPrefs, prefs, dailyDigest]
  );

  return { prefs, dailyDigest, toggleChannel, toggleDailyDigest };
}

/* ── Email connection hook ── */

function useEmailConnectionStatus(): EmailConnectionStatus {
  const [status, setStatus] = useState<EmailConnectionStatus>({
    loading: true,
    googleConnected: false,
    azureConnected: false,
  });

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getAppLink("google").catch(() => ({ connected: false })),
      getAppLink("azure").catch(() => ({ connected: false })),
    ]).then(([google, azure]) => {
      if (!cancelled) {
        setStatus({
          loading: false,
          googleConnected: !!google.connected,
          azureConnected: !!azure.connected,
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return status;
}

/* ── Sub-components ── */

function EmailConnectionBanner({
  emailStatus,
  onConnectEmail,
}: {
  emailStatus: EmailConnectionStatus;
  onConnectEmail: () => void;
}) {
  const { t } = useTranslation();

  if (emailStatus.loading) {
    return (
      <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 px-5 py-3.5 flex items-center gap-3 animate-pulse">
        <div className="w-5 h-5 rounded-full bg-gray-200" />
        <div className="h-4 w-48 bg-gray-200 rounded" />
      </div>
    );
  }

  const isConnected = emailStatus.googleConnected || emailStatus.azureConnected;
  const provider = emailStatus.googleConnected
    ? "Google"
    : emailStatus.azureConnected
      ? "Microsoft"
      : null;

  if (isConnected) {
    return (
      <div className="mt-6 rounded-lg border border-green-200 bg-green-50 px-5 py-3.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
          <div>
            <p className="text-sm font-medium text-green-900">{t("Email connected")}</p>
            <p className="text-xs text-green-700 mt-0.5">
              {t("Email notifications will be delivered via {{provider}}", { provider })}
            </p>
          </div>
        </div>
        <button
          onClick={onConnectEmail}
          className="text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1 shrink-0 cursor-pointer"
        >
          {t("Manage")}
          <ExternalLink className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-5 py-3.5 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
        <div>
          <p className="text-sm font-medium text-amber-900">{t("No email account connected")}</p>
          <p className="text-xs text-amber-700 mt-0.5">
            {t("Connect an email account to receive email notifications")}
          </p>
        </div>
      </div>
      <button
        onClick={onConnectEmail}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 transition-colors shrink-0 cursor-pointer"
      >
        {t("Connect email")}
        <ExternalLink className="w-3 h-3" />
      </button>
    </div>
  );
}

function DailyDigestSection({
  enabled,
  onToggle,
  emailConnected,
  onConnectEmail,
}: {
  enabled: boolean;
  onToggle: (val: boolean) => void;
  emailConnected: boolean;
  onConnectEmail: () => void;
}) {
  const { t } = useTranslation();
  const disabled = !emailConnected;

  return (
    <section className="mt-8">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">{t("Daily digest")}</h3>
      <div
        className={`rounded-lg border border-gray-200 bg-white px-5 py-4 flex items-center justify-between gap-4 ${disabled ? "opacity-50" : ""}`}
      >
        <div>
          <p className="text-sm font-medium text-gray-900">{t("Enable daily digest")}</p>
          <p className="text-sm text-gray-500 mt-0.5">
            {t(
              "Includes tasks overdue and due today. Sent every morning if any tasks are due or overdue."
            )}
          </p>
        </div>
        <Switch
          checked={disabled ? false : enabled}
          onChange={disabled ? () => {} : onToggle}
          disabled={disabled}
          className={`${
            !disabled && enabled ? "bg-blue-600" : "bg-gray-200"
          } relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
            disabled
              ? "cursor-not-allowed"
              : "cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
          }`}
        >
          <span className="sr-only">{t("Enable daily digest")}</span>
          <span
            className={`${
              !disabled && enabled ? "translate-x-4" : "translate-x-0"
            } pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
          />
        </Switch>
      </div>
      {disabled && (
        <div className="mt-2 flex items-center gap-2 px-1">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
          <span className="text-xs text-amber-700">
            {t("Daily digest requires an email connection.")}
          </span>
          <button
            onClick={onConnectEmail}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium cursor-pointer underline"
          >
            {t("Connect email account")}
          </button>
        </div>
      )}
    </section>
  );
}

function NotificationRow({
  labelKey,
  descKey,
  checked,
  onToggle,
  emailChecked,
  onToggleEmail,
  isLast,
  emailDisabled = false,
}: {
  labelKey: string;
  descKey: string;
  checked: boolean;
  onToggle: () => void;
  emailChecked: boolean;
  onToggleEmail: () => void;
  isLast: boolean;
  emailDisabled?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div
      className={`flex items-center px-5 py-3.5 ${
        !isLast ? "border-b border-gray-100" : ""
      } hover:bg-gray-50/40 transition-colors`}
    >
      <div className="flex-1 min-w-0 pr-4">
        <p className="text-sm font-medium text-gray-900">{t(labelKey)}</p>
        <p className="text-sm text-gray-500 mt-0.5">{t(descKey)}</p>
      </div>

      {/* Email */}
      <div className="w-20 flex justify-center">
        <Checkbox
          checked={emailDisabled ? false : emailChecked}
          onCheckedChange={emailDisabled ? undefined : onToggleEmail}
          disabled={emailDisabled}
          className={`h-4 w-4 rounded border-gray-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white ${
            emailDisabled ? "opacity-30 cursor-not-allowed" : "cursor-pointer"
          }`}
        />
      </div>

      {/* App */}
      <div className="w-20 flex justify-center">
        <Checkbox
          checked={checked}
          onCheckedChange={onToggle}
          className="h-4 w-4 rounded border-gray-300 cursor-pointer data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white"
        />
      </div>
    </div>
  );
}

function CollaborationSection({
  prefs,
  onToggleChannel,
  emailConnected,
  onConnectEmail,
}: {
  prefs: Record<string, ChannelPrefs>;
  onToggleChannel: (typeKey: string, channel: "inApp" | "email") => void;
  emailConnected: boolean;
  onConnectEmail: () => void;
}) {
  const { t } = useTranslation();
  const emailDisabled = !emailConnected;

  return (
    <section className="mt-8">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">
        {t("Collaboration notifications")}
      </h3>

      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        {/* Table header */}
        <div className="flex items-center px-5 py-3 border-b border-gray-100 bg-gray-50/60">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Bell className="w-4 h-4 text-gray-400" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {t("Notify me about")}
            </span>
          </div>
          <div
            className={`flex items-center gap-1.5 w-20 justify-center ${emailDisabled ? "opacity-40" : ""}`}
          >
            <Mail className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs font-medium text-gray-500">{t("Email")}</span>
            {emailDisabled && <AlertTriangle className="w-3 h-3 text-amber-500" />}
          </div>
          <div className="flex items-center gap-2 w-20 justify-center">
            <Smartphone className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs font-medium text-gray-500">{t("App")}</span>
          </div>
        </div>

        {/* Rows */}
        {NOTIFICATION_TYPES.map(({ key, labelKey, descKey }, idx) => (
          <NotificationRow
            key={key}
            labelKey={labelKey}
            descKey={descKey}
            emailChecked={prefs[key]?.email !== false}
            onToggleEmail={() => onToggleChannel(key, "email")}
            checked={prefs[key]?.inApp !== false}
            onToggle={() => onToggleChannel(key, "inApp")}
            isLast={idx === NOTIFICATION_TYPES.length - 1}
            emailDisabled={emailDisabled}
          />
        ))}
      </div>

      {/* Warning below table when email not connected */}
      {emailDisabled && (
        <div className="mt-3 flex items-center gap-2 px-1">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
          <span className="text-xs text-amber-700">{t("Email notifications are disabled.")}</span>
          <button
            onClick={onConnectEmail}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium cursor-pointer underline"
          >
            {t("Connect email account")}
          </button>
        </div>
      )}
    </section>
  );
}

/* ── Main Component ── */

export default function NotificationSetting() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId }: any = useParams();
  const { prefs, dailyDigest, toggleChannel, toggleDailyDigest } = useNotificationPreferences();
  const emailStatus = useEmailConnectionStatus();

  const handleConnectEmail = () => {
    navigate(`/workspaces/${projectId}/settings/user?item=mail&key=User%20Settings`);
  };

  return (
    <div className="max-w-2xl py-6">
      <h2 className="text-xl font-semibold text-gray-900">{t("Notifications")}</h2>
      <p className="mt-1 text-sm text-gray-500">
        {t("Customize your notification settings to stay informed without being overwhelmed")}
      </p>

      <EmailConnectionBanner emailStatus={emailStatus} onConnectEmail={handleConnectEmail} />

      <DailyDigestSection
        enabled={dailyDigest.enabled}
        onToggle={toggleDailyDigest}
        emailConnected={
          !emailStatus.loading && (emailStatus.googleConnected || emailStatus.azureConnected)
        }
        onConnectEmail={handleConnectEmail}
      />
      <CollaborationSection
        prefs={prefs}
        onToggleChannel={toggleChannel}
        emailConnected={
          !emailStatus.loading && (emailStatus.googleConnected || emailStatus.azureConnected)
        }
        onConnectEmail={handleConnectEmail}
      />
    </div>
  );
}
